import { useState } from 'react'
import { Link } from 'react-router-dom'
import { getClassifierById } from './mockData'
import { WIREFRAME_PATHS } from './routes'
import { ClassifierVersion } from './types'
import ModalityPill from '../../components/badges/ModalityPill'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote } from './designNotes'
import GoLiveModal from './components/GoLiveModal'
import DeleteClassifierModal from './components/DeleteClassifierModal'
import AddProfilesModal from './components/AddProfilesModal'
import ClassifierDetailsCard from './components/classifierEdit/ClassifierDetailsCard'
import PromptVersionCard from './components/classifierEdit/PromptVersionCard'
import VersionsRail from './components/classifierEdit/VersionsRail'
import LiveStatusCard from './components/classifierEdit/LiveStatusCard'
import AppliedToTable from './components/classifierEdit/AppliedToTable'

// Fixed wireframe subject — no :id routing needed at this stage.
const CLASSIFIER_ID = 'hate-harassment'

type ModalState = 'none' | 'goLive' | 'delete' | 'addProfiles' | 'rollback'

export default function ClassifierEditPage() {
    const c = getClassifierById(CLASSIFIER_ID)

    const [modal, setModal] = useState<ModalState>('none')

    // All hooks must be called unconditionally before any early return.
    const [selectedVersion, setSelectedVersion] = useState<ClassifierVersion | null>(
        c ? c.versions[0] : null
    )

    if (!c || !selectedVersion) {
        return <div className="p-8 text-gray-500">Classifier not found.</div>
    }

    const draftVersion = c.versions.find((v) => v.status === 'draft')
    const draftVersionNumber = draftVersion ? draftVersion.version : c.latestVersion

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <WireframeBanner />

            <PageDesignNote type="rationale">
                Create or edit a classifier and preview its effect before it goes live. Reached from
                the Classifier configuration screen.
                <br />
                <strong>
                    The classifier version can only be applied to all profiles at once or none at
                    all.
                </strong>
            </PageDesignNote>

            {/* Breadcrumb */}
            <nav className="px-6 pt-3 text-xs text-gray-500" aria-label="Breadcrumb">
                <Link
                    to={WIREFRAME_PATHS.library}
                    className="text-arwen-600 font-semibold hover:underline"
                >
                    Classifier configuration
                </Link>
                <span className="mx-1">/</span>
                <span className="text-gray-700 font-semibold">{c.title}</span>
            </nav>

            {/* Header */}
            <div className="px-6 pt-2 pb-4 flex items-center justify-between gap-4">
                <h1 className="font-sans !font-medium text-2xl text-gray-900 flex items-center gap-2 flex-wrap">
                    <span>
                        {c.title}
                        <span aria-hidden="true" className="text-arwen-600">
                            .
                        </span>
                    </span>
                    <ModalityPill modality={c.modality} />
                    <span className="inline-flex items-center text-[10.5px] font-semibold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">
                        Prompt · draft v{draftVersionNumber}
                    </span>
                </h1>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => setModal('delete')}
                        className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-severe hover:bg-red-50"
                        aria-label="Delete classifier"
                    >
                        Delete classifier
                    </button>
                </div>
            </div>

            {/* Body: two-column */}
            <div className="flex gap-4 px-6 pb-6 flex-1 items-start">
                {/* LEFT: details (not versioned) then prompt version (versioned) */}
                <div className="flex-1 min-w-0 space-y-4">
                    <ClassifierDetailsCard classifier={c} />
                    <PromptVersionCard
                        version={selectedVersion}
                        draftVersionNumber={draftVersionNumber}
                    />
                </div>

                {/* RIGHT: Versions + Live status */}
                <div className="w-72 flex-shrink-0 flex flex-col gap-3">
                    <VersionsRail
                        versions={c.versions}
                        selectedVersion={selectedVersion}
                        onSelect={setSelectedVersion}
                    />
                    <LiveStatusCard
                        classifier={c}
                        selectedVersion={selectedVersion}
                        draftVersionNumber={draftVersionNumber}
                        onGoLive={() => setModal('goLive')}
                        onRollback={() => setModal('rollback')}
                    />
                </div>
            </div>

            <div className="px-6 pb-8">
                <AppliedToTable
                    profileCount={c.profilesUsing}
                    onAdd={() => setModal('addProfiles')}
                />
            </div>

            <GoLiveModal
                open={modal === 'goLive'}
                onClose={() => setModal('none')}
                classifierId="hate-harassment"
                fromVersion={3}
                toVersion={4}
                profileCount={247}
                teamCount={11}
                orgCount={8}
            />
            <GoLiveModal
                open={modal === 'rollback'}
                onClose={() => setModal('none')}
                mode="rollback"
                classifierId="hate-harassment"
                fromVersion={c.liveVersion}
                toVersion={selectedVersion.version}
                profileCount={c.profilesUsing}
                teamCount={c.teamsUsing}
                orgCount={8}
            />
            <DeleteClassifierModal
                open={modal === 'delete'}
                onClose={() => setModal('none')}
                classifierId="hate-harassment"
                classifierName="Hate & Harassment"
            />
            <AddProfilesModal
                open={modal === 'addProfiles'}
                onClose={() => setModal('none')}
                classifierName={c.title}
            />
        </div>
    )
}
